import { useAuth0 } from '#~/auth/index.js';
import { selectStoredPreset } from '#~/control/preset/native-owner.js';
import * as Commands from '#~/control/preset/commands/index.js';
import { StudioError } from '#~/error/index.js';
import { logger } from '#~/logger/index.js';
import { comparatorFn, compareIds, getUniqueName } from '#~/models/index.js';
import { post, raiseError } from '#~/notifications/index.js';
import { usePreferences, useProducer } from '#~/preferences/hooks.js';
import { useRoom } from '#~/room/hooks.js';
import type { Wiring } from './types.js';
import { ensureNotNil } from '@studio/js-utils';
import { normalizeId } from '@studio/models';
import { isCalibrating as _isCalibrating } from '@studio/models/control/calibration';
import type { Group, Preset } from '@studio/models/control/preset';
import { PresetDefaults } from '@studio/models/control/preset';
import type { Device } from '@studio/models/device';
import { isEqual } from '@studio/models/ptz';
import type { Tuple } from '@studio/react-utils';
import { useDebouncedEffect, useDebouncedFn, useLatestFn } from '@studio/react-utils/hooks';
import { isEmpty, isNotEmpty } from '@tryiris-ai/js-types';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { pick } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { shallow } from 'zustand/shallow';

/** @internal */
const useSelectedGroup = (deviceId: Device['id']): Tuple<T_Maybe<Group['id']>, Group['id']> => {
  const key = `control.${deviceId}.preset.group`;

  const groupId = normalizeId<Group['id']>(usePreferences((state) => state.getValue(key)));
  const producer = useProducer();

  /** May be used before mount. Don't use `useLatestFn` */
  const setSelectedGroup = useCallback(
    (nextGroup: Group['id']) => {
      producer((draft) => {
        draft.setValue(key, nextGroup);
      });
    },
    [key]
  );

  return [groupId, setSelectedGroup];
};

/** @internal */
export function useWiring(deviceId: Device['id']): T_Maybe<Wiring> {
  /** Get presets and groups for this device. */
  const store = useRoom((µ) => {
    const control = µ.devices[deviceId]?.control;

    return {
      groups: control?.presets?.groups,
      isCalibrating: _isCalibrating(control?.calibration?.status),
      position: control?.movement?.position,
      presets: control?.presets?.items
    };
  }, shallow);

  const [selectedGroup, setSelectedGroup] = useSelectedGroup(deviceId);
  const [selectedPresetIds, setSelectedPresetIds] = useState<Preset['id'][]>([]);

  const { getAccessTokenSilently } = useAuth0();

  // Synchronize the selected position presets.
  const synchronize = useLatestFn(() => {
    const { isCalibrating, position, presets } = store;

    if (isCalibrating) {
      return;
    }

    const nextPresetIds =
      isNotEmpty(presets) && position
        ? Object.values(presets)
            .filter((preset) => preset.position && isEqual(preset.position, position))
            .map((µ) => µ.id)
            .sort((a, b) => a.localeCompare(b))
        : [];

    if (!compareIds(selectedPresetIds, nextPresetIds)) {
      setSelectedPresetIds(nextPresetIds);
    }
  });

  // When the camera position changes, recompute the selected presets.
  useDebouncedEffect(
    () => {
      synchronize();
    },
    [store.position],
    1e3
  );

  /*
   * Debounced thumbnail handler.  We give the camera a few seconds to move into
   * position before updating the thumbnail.
   */
  const updateThumbnail = useDebouncedFn(async (preset: Preset) => {
    const { position } = store;
    const token = await getAccessTokenSilently();

    // Is the camera close to the preset's position?
    if (!position || !preset.position || !isEqual(preset.position, position)) {
      logger.debug(`Camera not in position. Skipping thumbnail update for preset: ${preset.name}`);

      return;
    }

    logger.debug(`Updating thumbnail update for preset: ${preset.name}`);

    // Update camera thumbnail. Debounced background refresh: it retries on the
    // next preset move and asks nothing of the user, so it must not light the
    // bell (unlike the user-issued group/preset commands below).
    await Commands.updateThumbnail(deviceId, preset.id, token).catch(raiseError);
  }, 3e3);

  // Capability gating lives in the commands (`assertPresetAllowed`) and the
  // inert tray, not here. Rename/reorder/remove stay allowed on an unsupported
  // camera so stale presets can still be cleaned up.
  // Memoize handlers as best we can before conditionally bailing out.
  const handlers = useMemo(
    () => ({
      addGroup: async (args: Parameters<typeof Commands.addGroup>[1]): Promise<void> => {
        const token = await getAccessTokenSilently();

        Commands.addGroup(deviceId, args, token).catch((error) =>
          raiseError(error, { actionable: true })
        );
      },

      addPreset: async (
        gid: Group['id'],
        args: Parameters<typeof Commands.addPreset>[2]
      ): Promise<void> => {
        const token = await getAccessTokenSilently();

        await Commands.addPreset(deviceId, gid, args, token).catch((error) =>
          raiseError(error, { actionable: true })
        );
      },

      refreshPreset: (_presetId: Preset['id']): void => {},

      removeGroup: async (groupId: Group['id']): Promise<void> => {
        const token = await getAccessTokenSilently();

        Commands.removeGroup(deviceId, groupId, token).catch((error) =>
          raiseError(error, { actionable: true })
        );
      },

      removePreset: async (presetId: Preset['id']): Promise<void> => {
        const token = await getAccessTokenSilently();

        Commands.removePreset(deviceId, presetId, token).catch((error) =>
          raiseError(error, { actionable: true })
        );
      },

      renameGroup: async (groupId: Group['id'], nextName: string): Promise<void> => {
        const token = await getAccessTokenSilently();

        Commands.updateGroup(deviceId, groupId, { name: nextName }, token).catch((error) =>
          raiseError(error, { actionable: true })
        );
      },

      renamePreset: async (presetId: Preset['id'], nextName: string): Promise<void> => {
        const token = await getAccessTokenSilently();

        Commands.updatePreset(deviceId, presetId, { name: nextName }, token).catch((error) =>
          raiseError(error, { actionable: true })
        );
      },

      reorderGroups: async (groupIds: Group['id'][]): Promise<void> => {
        const token = await getAccessTokenSilently();

        Commands.reorderGroups(deviceId, groupIds, token).catch((error) =>
          raiseError(error, { actionable: true })
        );
      },

      reorderPresets: async (presetIds: Preset['id'][]): Promise<void> => {
        const token = await getAccessTokenSilently();

        Commands.reorderPresets(deviceId, presetIds, token).catch((error) =>
          raiseError(error, { actionable: true })
        );
      },

      selectGroup: (groupId: Group['id']): void => {
        setSelectedGroup(groupId);
      },

      selectPreset: (preset: Preset): void => {
        setSelectedPresetIds([preset.id]);

        selectStoredPreset(deviceId, preset)
          .then(() => {
            if (preset.position) {
              updateThumbnail(preset);
            }
          })
          .catch((cause) => {
            raiseError(new StudioError(`Unable to select preset: ${preset.name}`, cause), {
              actionable: true
            });
          });
      }
    }),
    [deviceId]
  );

  // We should have at least one preset and group.
  if (isEmpty(store.groups)) {
    return undefined;
  }

  // Sort the groups.
  const groups = Object.values(store.groups).sort(comparatorFn);

  // If we don't know what group is selected, select the first one.
  const selection = {
    groupId:
      selectedGroup && store.groups[selectedGroup]
        ? selectedGroup
        : // There will always be at least one group.
          ensureNotNil(groups[0]).id,
    presetIds: selectedPresetIds
  };

  // If the selected group is not the preferred group, update the preferred group.
  if (selection.groupId !== selectedGroup) {
    queueMicrotask(() => {
      setSelectedGroup(selection.groupId);
    });
  }

  // Find the presets associated with the selected group
  const presets = Object.values(store.presets ?? {})
    .filter((preset) => preset.groupId === selection.groupId)
    .sort(comparatorFn);

  const { isCalibrating } = store;

  return {
    ...pick(handlers, [
      'removeGroup',
      'removePreset',
      'renameGroup',
      'renamePreset',
      'reorderGroups',
      'reorderPresets',
      'selectGroup'
    ]),

    addGroup: (): void => {
      handlers.addGroup({
        name: getUniqueName(groups, {
          prefix: PresetDefaults.groupName
        }),
        rank: groups.length
      });
    },

    addPreset: (): void => {
      if (isCalibrating) {
        post({
          id: 'preset.add.calibrating',
          message: 'You cannot add a position preset while the camera is calibrating.',
          title: 'Add Preset'
        });

        return;
      }
      handlers.addPreset(selection.groupId, {
        name: getUniqueName(presets, {
          prefix: PresetDefaults.presetName
        }),
        rank: presets.length
      });
    },

    deviceId,
    groups,
    presets,

    refreshPreset: (presetId: Preset['id']): void => {
      if (isCalibrating) {
        post({
          id: 'preset.refresh.calibrating',
          message: 'The operation is not available while the camera is calibrating.',
          title: 'Refresh Preset'
        });

        return;
      }

      handlers.refreshPreset(presetId);
    },

    selectPreset: (presetId: Preset['id']): void => {
      if (isCalibrating) {
        post({
          id: 'preset.select.calibrating',
          message: 'You cannot select a position preset while the camera is calibrating.',
          title: 'Select Preset'
        });

        return;
      }

      const preset = store.presets?.[presetId];

      if (preset) {
        handlers.selectPreset(preset);
      }
    },
    selection
  };
}
